import { useEffect, useState } from 'preact/hooks';
import { Modal } from '@/ui/Modal.jsx';
import { Lightbox } from '@/ui/Lightbox.jsx';
import { absoluteImageUrl, fetchProductDetails } from '@/api';
import { addToCart } from '@/store/cart';
import { dashboard } from '@/store/dashboard';
import { notify } from '@/store/ui';
import { formatQuantity, numberFormat } from '@/utils/format';
import { translateSeason } from '@/utils/season';
import { detectProductType, getNestedValue, PRODUCT_SPECS_CONFIG } from './filter-config.js';
import css from './ProductModal.module.css';

export function ProductModal({ sku, onClose }) {
  const [product, setProduct] = useState(null);
  const [err, setErr] = useState(null);
  const [qty, setQty] = useState(1);
  const [showLightbox, setShowLightbox] = useState(false);

  useEffect(() => {
    let cancel = false;
    fetchProductDetails(sku)
      .then((data) => {
        if (!cancel) setProduct(data.item || data.product);
      })
      .catch((e) => !cancel && setErr(e));
    return () => {
      cancel = true;
    };
  }, [sku]);

  function handleAdd() {
    if (qty <= 0) return;
    addToCart(product, qty);
    notify(`${product.name} добавлен в корзину (${qty} шт.)`);
    onClose?.();
  }

  return (
    <Modal open size="lg" title={product?.name ?? 'Товар'} onClose={onClose}>
      {!product && !err && <p class="loading">Загрузка</p>}
      {err && <div class="empty-state">Ошибка: {err.message}</div>}
      {product && (
        <ProductDetails
          product={product}
          qty={qty}
          onQty={setQty}
          onAdd={handleAdd}
          onShowLightbox={() => setShowLightbox(true)}
        />
      )}
      {showLightbox && product?.image_url && (
        <Lightbox
          src={absoluteImageUrl(product.image_url)}
          alt={product.name}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </Modal>
  );
}

function ProductDetails({ product, qty, onQty, onAdd, onShowLightbox }) {
  let inStock = 0;
  let inTransit = 0;
  for (const av of product.availability ?? []) {
    const q = av.available ?? av.quantity ?? 0;
    const isTransit = av.warehouse?.is_transit === true || (av.warehouse?.name ?? '').toLowerCase().startsWith('в пути');
    if (isTransit) inTransit += q;
    else inStock += q;
  }

  const totalAvail = inStock + inTransit;
  const categoryCode = product.category?.code?.toLowerCase() ?? '';
  const categoryTitle = product.category?.title?.toLowerCase() ?? '';
  const type = detectProductType(categoryCode, categoryTitle);
  const specs = computeSpecs(product, type);
  const showMrc = dashboard.value?.use_custom_discount && product.mrc_price;

  return (
    <div class={css.card}>
      <div class={css.left}>
        {product.image_url ? (
          <button type="button" class={css.imageBox} onClick={onShowLightbox} title="Нажмите для увеличения">
            <img src={absoluteImageUrl(product.image_url)} alt={product.name} />
          </button>
        ) : (
          <div class={`${css.imageBox} ${css.noPhoto}`}>
            <span>📷</span>
            <p>Нет фото</p>
          </div>
        )}
      </div>
      <div class={css.right}>
        <h3>{product.name}</h3>
        <p class={css.sku}>Артикул: {product.sku}</p>
        <div class={css.specs}>
          {specs.map((s) => (
            <div key={s.label} class={css.spec}>
              <span class={css.specLabel}>{s.label}</span>
              <span>{s.value}</span>
            </div>
          ))}
        </div>

        <div class={css.order}>
          <div class={css.priceBlock}>
            <div class={css.price}>{numberFormat(product.opt_price ?? product.price)} ₽</div>
            {showMrc && <div class={css.mrc}>МРЦ: {numberFormat(product.mrc_price)} ₽</div>}
          </div>

          <div class={css.availability}>
            <h4>Наличие</h4>
            {totalAvail > 0 ? (
              <>
                <div class={css.availLine}>
                  <span>В наличии:</span>
                  <span>{formatQuantity(inStock)} шт.</span>
                </div>
                <div class={css.availLine}>
                  <span>В пути:</span>
                  <span>{formatQuantity(inTransit)} шт.</span>
                </div>
              </>
            ) : (
              <p class="empty-state">Нет на складах</p>
            )}
          </div>

          <div class={css.addForm}>
            {totalAvail > 0 ? (
              <>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  onInput={(e) => onQty(parseInt(e.target.value, 10) || 1)}
                  class={css.qtyInput}
                  aria-label="Количество"
                />
                <button class="btn btn-primary" type="button" onClick={onAdd}>
                  В корзину
                </button>
              </>
            ) : (
              <button class="btn btn-primary" disabled type="button">
                Нет в наличии
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function computeSpecs(product, type) {
  const config = PRODUCT_SPECS_CONFIG[type] || PRODUCT_SPECS_CONFIG.default;
  const tireSpec = product.tire_spec || {};

  return config
    .map((spec) => {
      let value;
      if (spec.format === 'tire_size') {
        const w = tireSpec.width || product.width;
        const p = tireSpec.profile || product.profile;
        const d = tireSpec.diameter || product.diameter;
        value = `${w ?? ''}/${p ?? ''} R${d ?? ''}`;
        if (value === '// R') value = null;
      } else if (spec.format === 'season') {
        value = translateSeason(getNestedValue(product, spec.key) || tireSpec.season);
      } else if (spec.format === 'boolean') {
        value = getNestedValue(product, spec.key) ? 'Да' : 'Нет';
      } else {
        value = getNestedValue(product, spec.key);
      }
      if (value && value !== '—' && spec.suffix) value = `${value}${spec.suffix}`;
      return { label: spec.label, value: value ?? '—' };
    })
    .filter((s) => s.value && s.value !== '—');
}
